import React, { useState } from "react"
import { Badge, Card, CardBody } from "reactstrap"
import { assetSrc } from "./sponsorFields"

// A sponsor band as the public page will draw it, made editable in place. Drag a plate to reorder
// it, drop it under another tier heading to re-tier it, click it to edit it.
//
// One editor for both bands CP administers — Sportya's own partners and the sponsors of one event
// (ADR-0016: one rendering component, two places to administer). What differs between them is
// copy: the title, what clicking a plate opens, what the footnote promises. Those come in as props;
// the wiring does not.
//
// Native HTML5 drag and drop, no library: sportya-cp has none, the interaction is a few large drop
// zones and 120px plates rather than a fine-grained sortable list, and the one existing "move this
// somewhere else" screen in CP (the Draw editor) is click-driven too. What native drag does not give
// is a keyboard path — so every move here must also be reachable from a table under the band, which
// is where an admin without a mouse arranges it.

// Mirrors the DS plate (SportyaSponsorsStrip, 0.4.0): 12px of padding, and a height chosen so the
// content box is square at the plate's own width — which is what lets a square logo render at full
// width instead of being bound by the height. The editor is a picture of the public band, so a plate
// that is the wrong shape here is a lie about what will ship.
const PLATE_HEIGHT = 120
const PLATE_PADDING = 12

const bandStyle = { background: "#0b1d23", padding: 20, borderRadius: 6 }

const tierHeadingStyle = {
  fontFamily: "monospace",
  fontSize: 11,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "#8fa3ab",
  marginBottom: 10,
  display: "flex",
  justifyContent: "space-between",
}

const zoneStyle = active => ({
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  minHeight: PLATE_HEIGHT + 16,
  border: `1px dashed ${active ? "#5aa9c7" : "#24424e"}`,
  background: active ? "#12313c" : "transparent",
  borderRadius: 8,
  padding: 8,
  alignItems: "flex-start",
})

const plateStyle = (tile, dragged, disabled) => ({
  width: tile,
  height: PLATE_HEIGHT,
  background: "#fff",
  border: 0,
  borderRadius: 10,
  padding: PLATE_PADDING,
  cursor: disabled ? "wait" : "grab",
  opacity: dragged ? 0.35 : 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
})

// Where the plate would land. A bar rather than a gap: the plates reflow as it is, and a hole
// opening up reads as "something was removed" instead of "this is the spot".
const Caret = () => (
  <div style={{ width: 3, height: PLATE_HEIGHT, borderRadius: 2, background: "#5aa9c7" }} />
)

const Plate = ({
  row,
  tile,
  dragged,
  disabled,
  editLabel,
  logoSrc,
  onEdit,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) => (
  <button
    type="button"
    draggable={!disabled}
    disabled={disabled}
    title={`${row.name} — drag to move, click to edit`}
    aria-label={`${row.name} — ${editLabel}`}
    style={plateStyle(tile, dragged, disabled)}
    onClick={() => onEdit(row)}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onDragOver={onDragOver}
    onDrop={onDrop}
  >
    <img
      src={logoSrc(row.logoUrl)}
      alt={row.name}
      draggable={false}
      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", pointerEvents: "none" }}
    />
  </button>
)

const AddPlate = ({ tile, onClick, label, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-label={label}
    title={label}
    style={{
      width: tile,
      height: PLATE_HEIGHT,
      borderRadius: 10,
      border: "1px dashed #3a5a66",
      background: "transparent",
      color: "#6d8894",
      fontSize: 22,
      lineHeight: 1,
    }}
  >
    +
  </button>
)

/**
 * `tiers` is what the band's `bandTiers` answers: `[{ key, label, rows }]`, every tier present.
 * `capacity` is its `bandCapacity`. `onMove({ id, toTier, beforeId })` receives the meaning of a
 * drop; `onEdit(row)` a click on a plate; `onAdd(tierKey)` a click on a tier's "+" plate.
 *
 * Copy: `title` heads the card; `description` follows the capacity text; `footnote`, when given,
 * closes the card (the system band promises a cache delay there, the event band promises nothing).
 * `editLabel` completes a plate's accessible name ("Head — edit sponsor"); `addLabel(tierLabel)`
 * names the "+" plate. `logoSrc` turns a stored `logoUrl` into an address.
 *
 * `disabled` freezes the band — nothing drags, nothing opens — for a band written whole while a
 * write is out: a second gesture would be built on a list the server is about to replace, and a
 * plate opened before the reply lands would be edited under an id the reply retires.
 */
const BandEditor = ({
  tiers,
  capacity,
  onMove,
  onEdit,
  onAdd,
  disabled = false,
  title,
  description,
  footnote,
  editLabel = "edit",
  addLabel = tierLabel => `Add to ${tierLabel}`,
  logoSrc = assetSrc,
}) => {
  const [dragging, setDragging] = useState(null)
  // Where the drop would land right now: the tier under the cursor, and the plate it would go in
  // front of (null meaning last in that tier).
  const [over, setOver] = useState(null)

  const clearDrag = () => {
    setDragging(null)
    setOver(null)
  }

  const startDrag = (event, row) => {
    if (disabled) return
    // Firefox refuses to start a drag unless something is written to the transfer, and the id is
    // what a drop outside this component would need anyway.
    event.dataTransfer.setData("text/plain", row.id)
    event.dataTransfer.effectAllowed = "move"
    setDragging(row.id)
  }

  const allowDrop = (event, tier, beforeId) => {
    if (!dragging) return
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "move"
    setOver({ tier, beforeId })
  }

  const drop = (event, tier, beforeId) => {
    if (!dragging) return
    event.preventDefault()
    event.stopPropagation()
    const id = dragging
    clearDrag()
    if (id !== beforeId) onMove({ id, toTier: tier, beforeId })
  }

  const caretAt = (tier, beforeId) =>
    dragging && over && over.tier === tier && over.beforeId === beforeId

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h5 className="mb-0">{title}</h5>
          <Badge color={capacity.tone} pill>
            {capacity.badge}
          </Badge>
        </div>
        <p className="text-muted" style={{ fontSize: 13 }}>
          {capacity.text} {description}
        </p>

        <div style={bandStyle}>
          {tiers.map(({ key, label, rows }) => (
            <div key={key} className="mb-4">
              <div style={tierHeadingStyle}>
                <span>{label}</span>
                <span style={{ color: "#4d6470" }}>{dragging ? "drop here" : ""}</span>
              </div>
              <div
                data-testid={`band-tier-${key}`}
                style={zoneStyle(Boolean(dragging) && over?.tier === key)}
                onDragOver={event => allowDrop(event, key, null)}
                onDrop={event => drop(event, key, null)}
              >
                {rows.map(row => (
                  <React.Fragment key={row.id}>
                    {caretAt(key, row.id) && <Caret />}
                    <Plate
                      row={row}
                      tile={capacity.tile}
                      dragged={dragging === row.id}
                      disabled={disabled}
                      editLabel={editLabel}
                      logoSrc={logoSrc}
                      onEdit={onEdit}
                      onDragStart={event => startDrag(event, row)}
                      onDragEnd={clearDrag}
                      onDragOver={event => allowDrop(event, key, row.id)}
                      onDrop={event => drop(event, key, row.id)}
                    />
                  </React.Fragment>
                ))}
                {caretAt(key, null) && <Caret />}
                <AddPlate
                  tile={capacity.tile}
                  disabled={disabled}
                  onClick={() => onAdd(key)}
                  label={addLabel(label)}
                />
              </div>
            </div>
          ))}
        </div>

        {footnote && (
          <p className="text-muted mb-0 mt-2" style={{ fontSize: 12 }}>
            {footnote}
          </p>
        )}
      </CardBody>
    </Card>
  )
}

export default BandEditor
